import { useMemo, useState } from 'react';
import { useUpdate, useList } from '@refinedev/core';
import { RadioTower, Save, Copy, Radio } from 'lucide-react';
import { notify } from '@/lib/notify';
import { RestreamTargetStatus } from '@masters-club/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RestreamTargetsField } from './RestreamTargetsField';
import { RestreamStatusPanel } from './RestreamStatusPanel';
import {
  type Livestream,
  type ExternalRestreamTarget,
  RESTREAM_PLATFORM_LABELS,
} from '../types';

/**
 * Таб «Рестрим» на странице трансляции. Самодостаточный: редактирует список
 * площадок и сохраняет его отдельным PATCH (не через общую форму), показывает
 * счётчик активных/в-эфире и live-статусы, умеет импортировать конфиг площадок
 * из другой трансляции (чтобы не вводить ключи заново).
 */

interface RestreamTabProps {
  livestream: Livestream;
  /** Можно ли редактировать (RBAC из родителя). */
  canEdit: boolean;
}

// Генерим временный id для импортированных/новых строк (бэк перезапишет своим).
function freshId(): string {
  return `new-${Math.random().toString(36).slice(2, 10)}`;
}

export function RestreamTab({ livestream, canEdit }: RestreamTabProps) {
  const [targets, setTargets] = useState<ExternalRestreamTarget[]>(
    livestream.externalRestreamTargets ?? [],
  );
  const [importFromId, setImportFromId] = useState<string>('');
  const updateMutation = useUpdate<Livestream>();
  const isSaving = updateMutation.mutation?.isPending ?? false;

  const relayStatuses = useMemo(
    () => livestream.relayStatuses ?? [],
    [livestream.relayStatuses],
  );

  // Счётчик: всего / включено / реально в эфире (relay status=LIVE).
  const counts = useMemo(() => {
    const total = targets.length;
    const enabled = targets.filter((t) => t.enabled).length;
    const liveStatuses = relayStatuses.filter((s) => s.status === RestreamTargetStatus.LIVE);
    const livePlatforms = liveStatuses
      .map((s) => RESTREAM_PLATFORM_LABELS[s.platform] ?? s.platform)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    return { total, enabled, live: liveStatuses.length, livePlatforms };
  }, [targets, relayStatuses]);

  // Список других трансляций с непустым конфигом рестрима — для импорта.
  const { result: othersResult } = useList<Livestream>({
    resource: 'livestreams',
    pagination: { pageSize: 100, mode: 'server' },
  });
  const importableStreams = useMemo(() => {
    const items = othersResult?.data ?? [];
    return items.filter(
      (s) => s.id !== livestream.id && (s.externalRestreamTargets?.length ?? 0) > 0,
    );
  }, [othersResult, livestream.id]);

  const handleImport = (sourceId: string) => {
    setImportFromId(sourceId);
    const source = importableStreams.find((s) => String(s.id) === sourceId);
    if (!source) {
      return;
    }
    // Копируем площадки с новыми временными id (чтобы не пересеклись статусы).
    const copied = (source.externalRestreamTargets ?? []).map((t) => ({
      ...t,
      id: freshId(),
    }));
    setTargets(copied);
    notify.info(`Импортировано площадок: ${copied.length}. Проверьте и сохраните.`);
  };

  const handleSave = () => {
    updateMutation.mutate(
      {
        resource: 'livestreams',
        id: livestream.id,
        values: { externalRestreamTargets: targets },
      },
      {
        onError: (error) => {
          const msg =
            error instanceof Error ? error.message : 'Не удалось сохранить площадки';
          notify.error(msg);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      {/* Счётчик + импорт */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <RadioTower className="h-4 w-4" />
            Рестрим на внешние площадки
          </CardTitle>
          <CardDescription className="text-xs">
            Поток уходит на каждую включённую площадку без перекодирования. Stream key —
            секрет из кабинета площадки.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">Всего: {counts.total}</Badge>
            <Badge variant="outline">Включено: {counts.enabled}</Badge>
            <Badge variant={counts.live > 0 ? 'default' : 'secondary'}>
              <Radio className="mr-1 h-3 w-3" />В эфире: {counts.live}
            </Badge>
            {counts.livePlatforms.length > 0 && (
              <span className="text-xs text-muted-foreground">
                ({counts.livePlatforms.join(', ')})
              </span>
            )}
          </div>

          {canEdit && importableStreams.length > 0 && (
            <div className="flex items-center gap-2">
              <Copy className="h-4 w-4 text-muted-foreground" />
              <div className="w-72">
                <Select value={importFromId} onValueChange={handleImport}>
                  <SelectTrigger>
                    <SelectValue placeholder="Скопировать площадки из трансляции…" />
                  </SelectTrigger>
                  <SelectContent>
                    {importableStreams.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.title} · {s.externalRestreamTargets?.length ?? 0} площадок
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Редактор площадок */}
      {canEdit ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Площадки</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <RestreamTargetsField value={targets} onChange={setTargets} />
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={isSaving}>
                <Save className="mr-2 h-4 w-4" />
                {isSaving ? 'Сохранение…' : 'Сохранить площадки'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Live-статусы relay — показываем только когда есть площадки. */}
      {targets.length > 0 && (
        <RestreamStatusPanel targets={targets} statuses={relayStatuses} />
      )}
    </div>
  );
}
